import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import _ from "lodash";
import { VirtualBackgroundConfig } from "../types";

export interface AgentServiceInterface extends ConfigurableInterface {
  connect(callback: Function, errorCallback?: Function): void;
  disconnect(callback: Function, errorCallback?: Function): void;
  getServiceInfo(callback: Function, errorCallback?: Function): void;
  startRecording(callback: Function, errorCallback?: Function): void;
  stopRecording(callback: Function, errorCallback?: Function): void;
  startUsbIp(
    params: {
      hostName: string;
      deviceAttachTimeout?: number;
      notificationCallbacks?: Record<
        | "connectedToServer"
        | "disconnectedFromServer"
        | "deviceAttached"
        | "deviceDetached"
        | "deviceAttachTimedOut"
        | "failedToAttachDevice",
        Function
      >;
    },
    callback: Function,
    errorCallback?: Function
  ): void;
  stopUsbIp(callback: Function, errorCallback?: Function): void;
  startIpPrinters(
    params: {
      printers: {
        ip_address: string;
        port: number;
        driver_model: string;
        name: string;
      }[];
      configurationFinishedCallback?: Function;
    },
    callback: Function,
    errorCallback?: Function
  ): void;
  stopIpPrinters(callback: Function, errorCallback?: Function): void;
  startVpn(profile: string, callback: Function, errorCallback?: Function): void;
  downloadVirtualBackground(
    params: {
      url: string;
      startedCallback?: Function;
      progressCallback?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void;
  setVirtualBackgroundConfig(
    config: Partial<VirtualBackgroundConfig>,
    callback?: Function,
    errorCallback?: Function
  ): void;
  getVirtualBackgroundCameras(
    callback?: Function,
    errorCallback?: Function
  ): void;
  startEnumerateDesktopSources(
    callbacks: {
      addedOrChanged?: Function;
      removed?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void;
  stopEnumerateDesktopSources(
    callback?: Function,
    errorCallback?: Function
  ): void;
  startBarcodeInput(
    desktopSourceId: string,
    inputEnabledToggleHotkey: string | null,
    callback?: Function,
    errorCallback?: Function
  ): void;
  sendBarcodeInput(
    data: object,
    callback?: Function,
    errorCallback?: Function
  ): void;
  stopBarcodeInput(callback?: Function, errorCallback?: Function): void;
}

type Callbacks = {
  disconnect?: Function;
  recording?: {
    error: Function;
  };
  usbIp?: {
    started?: Function;
    stopped?: Function;
    error?: Function;
    notification?: Record<
      | "connectedToServer"
      | "disconnectedFromServer"
      | "deviceAttached"
      | "deviceDetached"
      | "deviceAttachTimedOut"
      | "failedToAttachDevice",
      Function
    >;
  };
  ipPrinters?: {
    started?: Function;
    stopped?: Function;
    error?: Function;
    configurationFinished?: Function;
  };
  virtualBackground?: {
    download?: {
      started: Function;
      finished?: Function;
      error?: Function;
      progress?: Function;
    };
    config?: {
      success?: Function;
      error?: Function;
    };
    cameras?: {
      success?: Function;
      error?: Function;
    };
  };
  vpn?: {
    started?: Function;
    error?: Function;
  };
  desktopSource?: {
    addedOrChanged?: Function;
    removed?: Function;
  };
  barcode?: {
    started?: Function;
    stopped?: Function;
    error?: Function;
  };
  serviceInfo?: {
    success?: Function;
  };
};

export class AgentService extends AbstractService
  implements AgentServiceInterface {
  private socket: WebSocket | null = null;
  private callbacks: Callbacks = {};

  private reconnectionTimer: any | null = null;

  private checkSocket = (errCb?: Function): boolean => {
    if (!this.socket) {
      errCb?.("No socket");
      return false;
    }
    return true;
  };

  private sendMessage(message: any): void {
    this.socket?.send(JSON.stringify(message));
  }
  public connect(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["agentServicePort", "agentServiceKey"]);

    this.socket = new WebSocket(
      `ws://127.0.0.1:${this.config.agentServicePort}`,
      ["re-agent"]
    );

    this.socket.onopen = e => {
      this.sendMessage({
        type: "auth",
        data: {
          key: this.config.agentServiceKey
        }
      });
    };

    this.socket.onmessage = event => {
      const message = JSON.parse(event.data);
      if (message.type == "auth_failed") {
        errorCallback?.(
          `Agent Service authentication failed: (${message.data.message})`
        );
      } else if (message.type == "auth_ok") {
        callback();
      } else if (message.type == "protocol_error") {
        errorCallback?.("Seems your Agent Service is outdated");
        return;
      } else if (message.type == "recording_error") {
        this.callbacks.recording?.error?.(message.data?.message);

        this.callbacks = _.omit(this.callbacks, "recording") as Callbacks;
      } else if (message.type == "usbip_started") {
        this.callbacks.usbIp?.started?.();

        if (this.callbacks?.usbIp) {
          this.callbacks = _.assign(this.callbacks, {
            usbIp: _.omit(this.callbacks.usbIp, "started")
          }) as Callbacks;
        }
      } else if (message.type == "usbip_stopped") {
        this.callbacks.usbIp?.stopped?.();

        this.callbacks = _.omit(this.callbacks, "usbIp") as Callbacks;
      } else if (message.type == "usbip_error") {
        this.callbacks.usbIp?.error?.(message.data?.message);

        this.callbacks = _.omit(this.callbacks, "usbIp") as Callbacks;
      } else if (message.type == "usbip_notification") {
        const type = _.camelCase(message.data?.type) as
          | "connectedToServer"
          | "disconnectedFromServer"
          | "deviceAttached"
          | "deviceDetached"
          | "deviceAttachTimedOut"
          | "failedToAttachDevice";

        this.callbacks.usbIp?.notification?.[type]?.(message.data?.device);
      } else if (message.type == "ip_printers_started") {
        this.callbacks.ipPrinters?.started?.();

        _.unset(this.callbacks, "ipPrinters.started");
      } else if (message.type == "ip_printers_configuration_finished") {
        this.callbacks.ipPrinters?.configurationFinished?.(
          message.data?.warnings
        );

        _.unset(this.callbacks, "ipPrinters.configurationFinished");
      } else if (message.type == "ip_printers_stopped") {
        this.callbacks.ipPrinters?.stopped?.();

        this.callbacks = _.omit(this.callbacks, "ipPrinters") as Callbacks;
      } else if (message.type == "ip_printers_error") {
        this.callbacks.ipPrinters?.error?.(message.data?.message);

        this.callbacks = _.omit(this.callbacks, "ipPrinters") as Callbacks;
      } else if (message.type == "virtual_background_download_error") {
        this.callbacks.virtualBackground?.download?.error?.(
          message.data?.message
        );

        _.unset(this.callbacks, "virtualBackground.download");
      } else if (message.type == "virtual_background_download_started") {
        this.callbacks.virtualBackground?.download?.started?.();

        _.unset(this.callbacks, "virtualBackground.download.started");
      } else if (message.type == "virtual_background_download_finished") {
        this.callbacks.virtualBackground?.download?.finished?.();

        _.unset(this.callbacks, "virtualBackground.download");
      } else if (message.type == "virtual_background_download_progress") {
        this.callbacks.virtualBackground?.download?.progress?.(message.data);
      } else if (message.type == "virtual_background_config") {
        this.callbacks.virtualBackground?.config?.success?.(message.data);

        _.unset(this.callbacks, "virtualBackground.config.success");
      } else if (message.type == "virtual_background_config_error") {
        this.callbacks.virtualBackground?.config?.error?.(
          message.data?.message
        );

        _.unset(this.callbacks, "virtualBackground.config.error");
      } else if (message.type == "virtual_background_cameras") {
        this.callbacks.virtualBackground?.cameras?.success?.(
          message.data?.cameras
        );

        _.unset(this.callbacks, "virtualBackground.cameras.success");
      } else if (message.type == "virtual_background_cameras_error") {
        this.callbacks.virtualBackground?.cameras?.error?.(
          message.data?.message
        );

        _.unset(this.callbacks, "virtualBackground.cameras.error");
      } else if (message.type == "vpn_up") {
        this.callbacks.vpn?.started?.();

        _.unset(this.callbacks, "vpn.started");
      } else if (message.type == "vpn_error") {
        this.callbacks.vpn?.error?.(message.data?.message);

        _.unset(this.callbacks, "vpn");
      } else if (message.type == "desktop_source_added_or_changed") {
        this.callbacks.desktopSource?.addedOrChanged?.(message?.data);
      } else if (message.type == "desktop_source_removed") {
        this.callbacks.desktopSource?.removed?.(message?.data?.id);
      } else if (message.type == "barcode_input_started") {
        this.callbacks.barcode?.started?.();

        _.unset(this.callbacks, "barcode.started");
      } else if (message.type == "barcode_input_stopped") {
        this.callbacks.barcode?.stopped?.();

        _.unset(this.callbacks, "barcode");
      } else if (message.type == "barcode_input_error") {
        this.callbacks.barcode?.error?.(message.data?.message);

        _.unset(this.callbacks, "barcode");
      } else if (message.type == "service_info") {
        this.callbacks.serviceInfo?.success?.(message?.data);

        _.unset(this.callbacks, "serviceInfo");
      }
    };

    this.socket.onclose = event => {
      if (event.wasClean) {
        this.callbacks.disconnect?.(
          `Connection to Agent Service closed cleanly, code=${event.code} reason=${event.reason}`
        );
      } else {
        errorCallback?.(
          "Can not connect to the Agent Service. Please make sure service is installed and running."
        );

        console.error(
          `Connection to Agent Service has been lost. Will try to reconnect in 1 second`
        );

        this.reconnectionTimer = setTimeout(() => {
          console.log("Reconnecting to Agent Service ...");
          this.connect(callback, errorCallback);
        }, 1000);
      }

      this.callbacks = _.omit(this.callbacks, "disconnect") as Callbacks;

      this.socket = null;
    };

    this.socket.onerror = event => {
      errorCallback?.("Agent Service websocket error");
      this.socket = null;
    };
  }

  public disconnect(callback: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.callbacks.disconnect = callback;

    this.socket!.close();
    this.socket = null;

    clearTimeout(this.reconnectionTimer);
  }

  public startRecording(callback: Function, errorCallback?: Function): void {
    this.checkConfig("recordingParams");

    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (errorCallback) {
      this.callbacks.recording = {
        error: errorCallback
      };
    }

    this.sendMessage({
      type: "start_recording",
      data: {
        destination: {
          type: "brb_connector",
          url: this.config.recordingParams!.destinationUrl
        },
        screen: {
          enabled: this.config.recordingParams!.screenEnabled,
          resolution: this.config.recordingParams!.screenResolution,
          // eslint-disable-next-line @typescript-eslint/camelcase
          frame_rate: this.config.recordingParams!.screenFrameRate,
          // eslint-disable-next-line @typescript-eslint/camelcase
          bit_rate: this.config.recordingParams!.screenBitRate
        },
        speakers: {
          enabled: this.config.recordingParams!.speakersEnabled,
          // eslint-disable-next-line @typescript-eslint/camelcase
          bit_rate: this.config.recordingParams!.speakersBitRate
        },
        microphone: {
          enabled: this.config.recordingParams!.microphoneEnabled,
          // eslint-disable-next-line @typescript-eslint/camelcase
          bit_rate: this.config.recordingParams!.microphoneBitRate
        }
      }
    });

    callback();
  }

  public stopRecording(callback: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.sendMessage({
      type: "stop_recording"
    });

    callback();
  }

  public startUsbIp(
    params: {
      hostName: string;
      deviceAttachTimeout?: number;
      notificationCallbacks?: Record<
        | "connectedToServer"
        | "disconnectedFromServer"
        | "deviceAttached"
        | "deviceDetached"
        | "deviceAttachTimedOut"
        | "failedToAttachDevice",
        Function
      >;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.callbacks.usbIp = _.assign(
      this.callbacks.usbIp ?? {},
      params.notificationCallbacks
        ? {
            notification: params.notificationCallbacks
          }
        : {},
      callback ? { started: callback } : {},
      errorCallback ? { error: callback } : {}
    );

    this.sendMessage({
      type: "start_usbip",
      data: {
        // eslint-disable-next-line @typescript-eslint/camelcase
        host_name: params.hostName,
        // eslint-disable-next-line @typescript-eslint/camelcase
        device_attach_timeout: params.deviceAttachTimeout
      }
    });
  }

  public stopUsbIp(callback: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callback || errorCallback) {
      this.callbacks.usbIp = _.assign(
        this.callbacks.usbIp ?? {},
        callback ? { stopped: callback } : {},
        errorCallback ? { error: errorCallback } : {}
      );
    }

    this.sendMessage({
      type: "stop_usbip"
    });
  }

  public startIpPrinters(
    params: {
      printers: {
        ip_address: string;
        port: number;
        driver_model: string;
        name: string;
      }[];
      configurationFinishedCallback?: Function;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (params?.configurationFinishedCallback || callback || errorCallback) {
      this.callbacks.ipPrinters = _.assign(
        this.callbacks.ipPrinters ?? {},
        params?.configurationFinishedCallback
          ? { configurationFinished: params.configurationFinishedCallback }
          : {},
        callback ? { started: callback } : {},
        errorCallback ? { error: errorCallback } : {}
      );
    }

    this.sendMessage({
      type: "start_ip_printers",
      data: {
        printers: params.printers
      }
    });
  }

  public stopIpPrinters(callback: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callback || errorCallback) {
      this.callbacks.ipPrinters = _.assign(
        this.callbacks.ipPrinters ?? {},
        callback ? { stopped: callback } : {},
        errorCallback ? { error: errorCallback } : {}
      );
    }

    this.sendMessage({
      type: "stop_ip_printers"
    });
  }
  public startVpn(
    profile: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callback || errorCallback) {
      this.callbacks.vpn = _.assign(
        callback ? { started: callback } : {},
        errorCallback ? { error: errorCallback } : {}
      );
    }

    this.sendMessage({
      type: "start_vpn",
      data: {
        profile
      }
    });
  }

  public downloadVirtualBackground(
    params: {
      url: string;
      startedCallback?: Function;
      progressCallback?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (
      callback ||
      errorCallback ||
      params.startedCallback ||
      params.progressCallback
    ) {
      _.set(
        this.callbacks,
        "virtualBackground.download",
        _.assign(
          callback ? { finished: callback } : {},
          errorCallback ? { error: errorCallback } : {},
          params.startedCallback ? { started: params.startedCallback } : {},
          params.progressCallback ? { progress: params.progressCallback } : {}
        )
      );
    }

    this.sendMessage({
      type: "download_virtual_background",
      data: {
        url: params.url
      }
    });
  }

  public setVirtualBackgroundConfig(
    config: Partial<VirtualBackgroundConfig>,
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }
    if (callback || errorCallback) {
      _.set(
        this.callbacks,
        "virtualBackground.config",
        _.assign(
          callback ? { success: callback } : {},
          errorCallback ? { error: errorCallback } : {}
        )
      );
    }

    this.sendMessage({
      type: "set_virtual_background_config",
      data: config
    });
  }

  public getVirtualBackgroundCameras(
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }
    if (callback || errorCallback) {
      _.set(
        this.callbacks,
        "virtualBackground.cameras",
        _.assign(
          callback ? { success: callback } : {},
          errorCallback ? { error: errorCallback } : {}
        )
      );
    }

    this.sendMessage({
      type: "get_virtual_background_cameras"
    });
  }

  public startEnumerateDesktopSources(
    callbacks: {
      addedOrChanged?: Function;
      removed?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callbacks.addedOrChanged || callbacks.removed) {
      _.set(this.callbacks, "desktopSource", callbacks);
    }

    this.sendMessage({
      type: "start_enumerate_desktop_sources",

      data: {
        types: ["entire_desktop", "window"],
        thumbnails: {
          // eslint-disable-next-line @typescript-eslint/camelcase
          max_width: 256,
          // eslint-disable-next-line @typescript-eslint/camelcase
          max_height: 256
        }
      }
    });

    callback?.();
  }

  public stopEnumerateDesktopSources(
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.sendMessage({
      type: "stop_enumerate_desktop_sources"
    });

    _.unset(this.callbacks, "desktopSource");

    callback?.();
  }

  public startBarcodeInput(
    desktopSourceId: string,
    pauseHotkey: string | null,
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callback || errorCallback) {
      _.set(
        this.callbacks,
        "barcode",
        _.assign(
          callback ? { started: callback } : {},
          errorCallback ? { error: callback } : {}
        )
      );
    }

    this.sendMessage({
      type: "start_barcode_input",
      data: {
        // eslint-disable-next-line @typescript-eslint/camelcase
        desktop_source_id: desktopSourceId,
        // eslint-disable-next-line @typescript-eslint/camelcase
        pause_hotkey: pauseHotkey
      }
    });
  }
  public sendBarcodeInput(
    data: object,
    callback?: Function,
    errorCallback?: Function
  ): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.sendMessage({
      type: "send_barcode_input",
      data
    });

    callback?.();
  }

  public stopBarcodeInput(callback?: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    this.sendMessage({
      type: "stop_barcode_input"
    });

    callback?.();
  }

  public getServiceInfo(callback: Function, errorCallback?: Function): void {
    if (!this.checkSocket(errorCallback)) {
      return;
    }

    if (callback) {
      _.set(this.callbacks, "serviceInfo", {
        success: callback
      });
    }

    this.sendMessage({
      type: "get_service_info"
    });
  }
}

export class StagingAgentService extends AbstractService
  implements AgentServiceInterface {
  public getServiceInfo(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }
  public connect(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public disconnect(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public startRecording(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public stopRecording(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public startUsbIp(
    params: {
      hostName: string;
      deviceAttachTimeout?: number;
      notificationCallbacks?: Record<
        | "connectedToServer"
        | "disconnectedFromServer"
        | "deviceAttached"
        | "deviceDetached"
        | "deviceAttachTimedOut"
        | "failedToAttachDevice",
        Function
      >;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public stopUsbIp(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public startIpPrinters(
    params: {
      printers: {
        ip_address: string;
        port: number;
        driver_model: string;
        name: string;
      }[];
      configurationFinishedCallback?: Function;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public stopIpPrinters(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public startVpn(
    profile: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public downloadVirtualBackground(
    params: {
      url: string;
      startedCallback?: Function;
      progressCallback?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }
  public setVirtualBackgroundConfig(
    config: Partial<VirtualBackgroundConfig>,
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }
  public getVirtualBackgroundCameras(
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public sendBarcodeInput(
    data: object,
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public startBarcodeInput(
    desktopSourceId: string,
    inputEnabledToggleHotkey: string | null,
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public startEnumerateDesktopSources(
    callbacks: {
      addedOrChanged?: Function;
      removed?: Function;
    },
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public stopBarcodeInput(callback?: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public stopEnumerateDesktopSources(
    callback?: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }
}
